import React from 'react';
import { DroppableProvided } from 'react-beautiful-dnd';
import RenderWidgetV2 from './RenderWidgetV2';
import { VerticalWidgetMenu } from './WidgetMenu';
import { StyledWidgetList } from '../shared/styled-components/Page';
import { SectionLayout, WidgetTypes } from '../../types';
import { WidgetPosition } from '../../constants/widgets';
import { useGuideStore } from '../../stores/guideStore';

interface Props {
  widgets: WidgetTypes[];
  pageId: string;
  sectionId?: string;
  columnIndex: number;
  droppableProvided?: DroppableProvided;
  numberOfColumns: number;

  contentSpacing: number;
  isHovering?: boolean;
  sectionLayout: SectionLayout;
}

const calculateWidgetPosition = (
  columnIndex: number,
  sectionLayout: SectionLayout,
) => {
  switch (sectionLayout) {
    case SectionLayout.SINGLE:
      return WidgetPosition.ONE_COLUMN;

    case SectionLayout.DOUBLE_50_50:
      return WidgetPosition.TWO_COLUMNS_EQUAL;

    case SectionLayout.DOUBLE_33_66:
      if (columnIndex === 0) {
        return WidgetPosition.TWO_COLUMNS_SMALL;
      }

      return WidgetPosition.TWO_COLUMNS_LARGE;

    case SectionLayout.DOUBLE_66_33:
      if (columnIndex === 1) {
        return WidgetPosition.TWO_COLUMNS_SMALL;
      }

      return WidgetPosition.TWO_COLUMNS_LARGE;

    case SectionLayout.TRIPLE_33_33_33:
      return WidgetPosition.THREE_COLUMNS_EQUAL;
    default:
      return WidgetPosition.ONE_COLUMN;
  }
};

const WidgetList: React.FC<Props> = ({
  widgets,
  sectionId,
  columnIndex,
  droppableProvided,
  numberOfColumns,
  sectionLayout,
  contentSpacing,
}) => {
  const isEditMode = useGuideStore(s => s.isEditMode);

  return (
    <StyledWidgetList
      data-testid="column"
      $contentSpacing={contentSpacing}
      $sectionLayout={sectionLayout}
      ref={droppableProvided && droppableProvided.innerRef}
      $isEmptyList={!widgets?.length}
    >
      {widgets.map((widget: WidgetTypes, index: number) => (
        <RenderWidgetV2
          index={index}
          key={widget.id}
          widget={widget}
          sectionId={sectionId}
          columnIndex={columnIndex}
          widgetPosition={calculateWidgetPosition(columnIndex, sectionLayout)}
          numberOfColumns={numberOfColumns}
        />
      ))}

      {droppableProvided && droppableProvided.placeholder}

      {isEditMode && (
        <VerticalWidgetMenu sectionId={sectionId} columnIndex={columnIndex} />
      )}
    </StyledWidgetList>
  );
};

export default WidgetList;
